import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import StoreProductCard, {
  type StoreProductCardData,
} from "@/components/store/StoreProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/store/storeCart";
import { Search, X, SlidersHorizontal, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";

type SortOption = "newest" | "price_asc" | "price_desc";

const BOOKS_PER_PAGE = 20;
const BOOKS_SLUG = "books";

type Category = { id: string; name: string; description: string | null };

type ProductWithMeta = StoreProductCardData & {
  metadata?: { author?: string; genre?: string | string[]; [k: string]: unknown };
};

/** Debounce helper */
function useDebounce<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

const StoreCategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const isBooks = slug === BOOKS_SLUG;

  const [category, setCategory] = useState<Category | null>(null);
  // Non-books: standard product list
  const [products, setProducts] = useState<ProductWithMeta[]>([]);
  // Books: server-side results
  const [bookResults, setBookResults] = useState<ProductWithMeta[]>([]);
  const [bookTotalCount, setBookTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [booksLoading, setBooksLoading] = useState(false);

  // Filter state
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  const [selectedAuthor, setSelectedAuthor] = useState("__all__");
  const [selectedGenre, setSelectedGenre] = useState("__all__");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 0]);
  const [priceInitialised, setPriceInitialised] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [page, setPage] = useState(1);

  // Filter options from server
  const [authors, setAuthors] = useState<string[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [globalMin, setGlobalMin] = useState(0);
  const [globalMax, setGlobalMax] = useState(0);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [debouncedQuery, selectedAuthor, selectedGenre, priceRange, sortBy]);

  // ─── Fetch category header (both paths) ───
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: cat } = await supabase
        .from("store_categories")
        .select("id, name, description")
        .eq("slug", slug)
        .maybeSingle();
      if (cancelled) return;
      if (!cat) {
        setCategory(null);
        setProducts([]);
        setLoading(false);
        return;
      }
      setCategory(cat as Category);

      if (!isBooks) {
        // Non-books: simple fetch
        const { data: prods } = await supabase
          .from("store_products")
          .select("id, slug, title, base_price_cents, currency, images, vendor:store_vendors_public!vendor_id(slug, display_name)")
          .eq("status", "active")
          .eq("category_id", cat.id)
          .order("created_at", { ascending: false })
          .limit(200);
        if (!cancelled) setProducts((prods as unknown as ProductWithMeta[]) ?? []);
      }

      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug, isBooks]);

  // ─── Books: fetch filter options once ───
  useEffect(() => {
    if (!isBooks) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("fn_store_book_filter_options");
      if (cancelled || !data) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return;
      setAuthors(row.authors ?? []);
      setGenres(row.genres ?? []);
      setGlobalMin(row.min_price ?? 0);
      setGlobalMax(row.max_price ?? 0);
      if (!priceInitialised && (row.min_price ?? 0) < (row.max_price ?? 0)) {
        setPriceRange([row.min_price, row.max_price]);
        setPriceInitialised(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBooks]);

  // ─── Books: server-side search/filter/sort/paginate ───
  const fetchIdRef = useRef(0);
  const fetchBooks = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setBooksLoading(true);
    const params: Record<string, unknown> = {
      _sort: sortBy,
      _page: page,
      _per_page: BOOKS_PER_PAGE,
    };
    if (debouncedQuery.trim()) params._query = debouncedQuery.trim();
    if (selectedAuthor !== "__all__") params._author = selectedAuthor;
    if (selectedGenre !== "__all__") params._genre = selectedGenre;
    if (priceInitialised && priceRange[0] > globalMin) params._price_min = priceRange[0];
    if (priceInitialised && priceRange[1] < globalMax) params._price_max = priceRange[1];

    const { data, error } = await supabase.rpc("fn_store_search_books", params as any);
    if (fetchIdRef.current !== id) return; // stale
    if (error) {
      console.error("fn_store_search_books error", error);
      setBooksLoading(false);
      return;
    }
    const rows = (data ?? []) as Array<{
      id: string; slug: string; title: string;
      base_price_cents: number; currency: string;
      images: unknown; metadata: unknown;
      vendor_slug: string | null; vendor_display_name: string | null;
      total_count: number;
    }>;
    const mapped: ProductWithMeta[] = rows.map((r) => ({
      id: r.id,
      slug: r.slug,
      title: r.title,
      base_price_cents: r.base_price_cents,
      currency: r.currency,
      images: r.images as string[] | null,
      metadata: r.metadata as ProductWithMeta["metadata"],
      vendor: r.vendor_slug ? { slug: r.vendor_slug, display_name: r.vendor_display_name ?? "" } : undefined,
    }));
    setBookResults(mapped);
    setBookTotalCount(rows[0]?.total_count ?? 0);
    setBooksLoading(false);
  }, [debouncedQuery, selectedAuthor, selectedGenre, priceRange, priceInitialised, sortBy, page, globalMin, globalMax]);

  useEffect(() => {
    if (isBooks && category) fetchBooks();
  }, [isBooks, category, fetchBooks]);

  const totalPages = Math.max(1, Math.ceil(bookTotalCount / BOOKS_PER_PAGE));

  const hasActiveFilters =
    query !== "" ||
    selectedAuthor !== "__all__" ||
    selectedGenre !== "__all__" ||
    sortBy !== "newest" ||
    (priceInitialised &&
      (priceRange[0] !== globalMin || priceRange[1] !== globalMax));

  const clearFilters = () => {
    setQuery("");
    setSelectedAuthor("__all__");
    setSelectedGenre("__all__");
    setSortBy("newest");
    setPriceRange([globalMin, globalMax]);
  };

  // Which list to render
  const displayList = isBooks ? bookResults : products;
  const isLoadingContent = loading || (isBooks && booksLoading);
  // Show filter bar once we have filter options (even if no products yet)
  const showBookControls = isBooks && !loading && (authors.length > 0 || genres.length > 0 || globalMax > 0);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-7xl px-6 py-8">
        <Link
          to="/store"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to Store
        </Link>
        {!category && !loading ? (
          <Card className="p-12 text-center mt-6">
            <h1 className="text-xl font-semibold">Category not found</h1>
          </Card>
        ) : (
          <>
            <h1 className="mt-3 text-3xl font-bold">
              {category?.name ?? "Loading…"}
            </h1>
            {category?.description && (
              <p className="mt-2 text-muted-foreground max-w-2xl">
                {category.description}
              </p>
            )}

            {/* Search + filter bar (books category) */}
            {showBookControls && (
              <div className="mt-6 space-y-4">
                {/* Search row */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative flex-1 max-w-md min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by title, author, or genre…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="pl-9"
                    />
                    {query && (
                      <button
                        onClick={() => setQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <Button
                    variant={showFilters ? "secondary" : "outline"}
                    size="sm"
                    onClick={() => setShowFilters((v) => !v)}
                    className="gap-1.5"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    Filters
                    {hasActiveFilters && (
                      <Badge
                        variant="default"
                        className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]"
                      >
                        !
                      </Badge>
                    )}
                  </Button>
                  {/* Sort */}
                  <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                    <SelectTrigger className="w-[170px] h-9 text-sm">
                      <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="price_asc">Price: Low → High</SelectItem>
                      <SelectItem value="price_desc">Price: High → Low</SelectItem>
                    </SelectContent>
                  </Select>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-muted-foreground"
                    >
                      Clear all
                    </Button>
                  )}
                </div>

                {/* Collapsible filter panel */}
                {showFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-lg border border-border bg-muted/30">
                    {/* Author */}
                    {authors.length > 0 && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Author
                        </label>
                        <Select
                          value={selectedAuthor}
                          onValueChange={setSelectedAuthor}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All authors" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All authors</SelectItem>
                            {authors.map((a) => (
                              <SelectItem key={a} value={a}>
                                {a}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Genre */}
                    {genres.length > 0 && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Genre
                        </label>
                        <Select
                          value={selectedGenre}
                          onValueChange={setSelectedGenre}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All genres" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All genres</SelectItem>
                            {genres.map((g) => (
                              <SelectItem key={g} value={g}>
                                {g}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Price range */}
                    {globalMax > globalMin && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Price range
                        </label>
                        <Slider
                          min={globalMin}
                          max={globalMax}
                          step={100}
                          value={priceRange}
                          onValueChange={(v) =>
                            setPriceRange(v as [number, number])
                          }
                          className="mt-3"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{formatPrice(priceRange[0], "usd")}</span>
                          <span>{formatPrice(priceRange[1], "usd")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Result count + page info */}
                <p className="text-sm text-muted-foreground">
                  {bookTotalCount}{" "}
                  {bookTotalCount === 1 ? "book" : "books"}
                  {hasActiveFilters ? " found" : ""}
                  {totalPages > 1 && ` · Page ${page} of ${totalPages}`}
                </p>
              </div>
            )}

            <div className="mt-6">
              {isLoadingContent ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="aspect-square rounded-lg" />
                  ))}
                </div>
              ) : displayList.length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-muted-foreground">
                    {hasActiveFilters
                      ? "No books match your filters. Try adjusting your search."
                      : "No products in this category yet. Check back soon."}
                  </p>
                  {hasActiveFilters && (
                    <Button
                      variant="link"
                      onClick={clearFilters}
                      className="mt-2"
                    >
                      Clear filters
                    </Button>
                  )}
                </Card>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {displayList.map((p) => (
                      <StoreProductCard
                        key={p.id}
                        product={{ ...p, categorySlug: slug }}
                      />
                    ))}
                  </div>

                  {/* Pagination controls */}
                  {isBooks && totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="gap-1"
                      >
                        <ChevronLeft className="h-4 w-4" /> Previous
                      </Button>
                      <span className="text-sm text-muted-foreground px-3">
                        {page} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="gap-1"
                      >
                        Next <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreCategoryPage;
